/**
 * Fuzzy Extractor Implementation - Ultra-Robust Version
 *
 * Generates consistent cryptographic keys from noisy biometric data (face descriptors).
 * This version prioritizes reliability over cryptographic purity:
 * - 1-bit quantization for maximum tolerance
 * - Stores normalized descriptor for similarity checking
 * - Aggressive bit-flip recovery (512+ combinations)
 * - Falls back to similarity-based key release if very close match
 */

import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, concatBytes } from '@noble/hashes/utils.js';
import type { FuzzyExtractorResult } from '../types';

// ============================================================================
// Constants
// ============================================================================

const DESCRIPTOR_LENGTH = 128; // face-api.js descriptor length
const KEY_LENGTH = 32; // Output key length in bytes

// Similarity thresholds for face matching
const SIMILARITY_THRESHOLD_STRICT = 0.92; // Very confident match
const SIMILARITY_THRESHOLD_LOOSE = 0.85; // Acceptable match with fuzzy recovery

// Maximum bit flips to try during reproduction
const MAX_BIT_FLIP_DEPTH = 10; // Try up to 10 most uncertain bits
const MAX_BIT_FLIP_ATTEMPTS = 512; // Total combinations to try

// ============================================================================
// Types
// ============================================================================

interface EnhancedHelperData {
  version: number;
  helper: Uint8Array;
  checksum: Uint8Array;
  stabilityMask: Uint8Array;
  enrollmentDescriptor: Float32Array; // Store for similarity comparison
  seed: Uint8Array; // Encrypted seed for direct recovery on high similarity
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate cosine similarity between two descriptors
 * Returns value between -1 and 1 (1 = identical)
 */
function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  const magnitude = Math.sqrt(normA) * Math.sqrt(normB);
  if (magnitude === 0) return 0;

  return dotProduct / magnitude;
}

/**
 * Calculate Euclidean distance between two descriptors
 */
function euclideanDistance(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return Infinity;

  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    sum += Math.pow(a[i] - b[i], 2);
  }
  return Math.sqrt(sum);
}

/**
 * Simple XOR-based encryption for seed storage
 * Not cryptographically strong but provides obfuscation
 */
function xorEncrypt(data: Uint8Array, key: Uint8Array): Uint8Array {
  const result = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    result[i] = data[i] ^ key[i % key.length];
  }
  return result;
}

/**
 * Derive an encryption key from descriptor
 */
function deriveEncryptionKey(descriptor: Float32Array): Uint8Array {
  // Create a stable key from the most significant bits of descriptor
  const stableBytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    // Use sign and magnitude of descriptor values
    const idx = i * 4;
    const val1 = descriptor[idx] > 0 ? 1 : 0;
    const val2 = descriptor[idx + 1] > 0 ? 2 : 0;
    const val3 = descriptor[idx + 2] > 0 ? 4 : 0;
    const val4 = descriptor[idx + 3] > 0 ? 8 : 0;
    const mag1 = Math.abs(descriptor[idx]) > 0.1 ? 16 : 0;
    const mag2 = Math.abs(descriptor[idx + 1]) > 0.1 ? 32 : 0;
    const mag3 = Math.abs(descriptor[idx + 2]) > 0.1 ? 64 : 0;
    const mag4 = Math.abs(descriptor[idx + 3]) > 0.1 ? 128 : 0;
    stableBytes[i] = val1 | val2 | val3 | val4 | mag1 | mag2 | mag3 | mag4;
  }
  return sha256(stableBytes);
}

// ============================================================================
// Fuzzy Extractor Class
// ============================================================================

export class FuzzyExtractor {
  /**
   * Enrollment: Generate key and helper data from face descriptor
   */
  gen(faceDescriptor: Float32Array): FuzzyExtractorResult {
    console.log('[ZK FuzzyExtractor] gen() called');

    // 1. Validate input
    if (faceDescriptor.length !== DESCRIPTOR_LENGTH) {
      throw new Error(`Invalid descriptor length: expected ${DESCRIPTOR_LENGTH}, got ${faceDescriptor.length}`);
    }

    // 2. Generate random seed
    const randomSeed = new Uint8Array(KEY_LENGTH);
    crypto.getRandomValues(randomSeed);

    // 3. Quantize descriptor to bits
    const { bits: wBits, stabilityMask, uncertaintyScores } = this.quantizeWithStability(faceDescriptor);
    console.log('[ZK FuzzyExtractor] Quantized to', wBits.length, 'bytes');

    // 4. Expand seed to match wBits length
    const expandedSeed = this.expandSeed(randomSeed, wBits.length);

    // 5. Compute helper: P = wBits XOR expandedSeed
    const helper = this.xor(wBits, expandedSeed);

    // 6. Encrypt seed with descriptor-derived key for direct recovery
    const encryptionKey = deriveEncryptionKey(faceDescriptor);
    const encryptedSeed = xorEncrypt(randomSeed, encryptionKey);

    // 7. Derive output key
    const key = this.deriveKey(randomSeed);

    // 8. Encode helper data
    const helperData = this.encodeHelper({
      version: 3,
      helper,
      checksum: sha256(randomSeed).slice(0, 4),
      stabilityMask,
      enrollmentDescriptor: faceDescriptor,
      seed: encryptedSeed,
    });

    console.log('[ZK FuzzyExtractor] Generated helper data, length:', helperData.length);

    return { key, helperData };
  }

  /**
   * Reproduction: Recover key from face descriptor + helper data
   */
  rep(faceDescriptor: Float32Array, helperData: string): Uint8Array | null {
    console.log('[ZK FuzzyExtractor] rep() called');

    // 1. Validate input
    if (faceDescriptor.length !== DESCRIPTOR_LENGTH) {
      throw new Error(`Invalid descriptor length`);
    }

    // 2. Decode helper data
    const decoded = this.decodeHelper(helperData);
    console.log('[ZK FuzzyExtractor] Helper data version:', decoded.version);

    // 3. Calculate similarity with enrollment descriptor
    if (decoded.enrollmentDescriptor) {
      const similarity = cosineSimilarity(faceDescriptor, decoded.enrollmentDescriptor);
      const distance = euclideanDistance(faceDescriptor, decoded.enrollmentDescriptor);
      console.log('[ZK FuzzyExtractor] Face similarity:', similarity.toFixed(4));
      console.log('[ZK FuzzyExtractor] Euclidean distance:', distance.toFixed(4));

      // High similarity - try direct seed decryption
      if (similarity >= SIMILARITY_THRESHOLD_STRICT && decoded.seed) {
        console.log('[ZK FuzzyExtractor] High similarity! Trying direct decryption...');
        const encryptionKey = deriveEncryptionKey(faceDescriptor);
        const decryptedSeed = xorEncrypt(decoded.seed, encryptionKey);
        const computedChecksum = sha256(decryptedSeed).slice(0, 4);

        if (this.arraysEqual(computedChecksum, decoded.checksum)) {
          console.log('[ZK FuzzyExtractor] Direct decryption successful!');
          return this.deriveKey(decryptedSeed);
        }
        console.log('[ZK FuzzyExtractor] Direct decryption failed, trying fuzzy recovery...');
      }

      // Low similarity - reject early
      if (similarity < SIMILARITY_THRESHOLD_LOOSE) {
        console.log('[ZK FuzzyExtractor] Similarity too low, rejecting');
        return null;
      }
    }

    // 4. Try fuzzy extraction with bit flipping
    const { bits: wPrimeBits } = this.quantizeWithStability(faceDescriptor);

    // 5. Compute noisy seed
    const noisySeed = this.xor(wPrimeBits, decoded.helper);
    const extractedSeed = noisySeed.slice(0, KEY_LENGTH);

    // 6. Check if exact match
    const computedChecksum = sha256(extractedSeed).slice(0, 4);
    if (this.arraysEqual(computedChecksum, decoded.checksum)) {
      console.log('[ZK FuzzyExtractor] Exact match!');
      return this.deriveKey(extractedSeed);
    }

    // 7. Try bit flipping on unstable dimensions
    console.log('[ZK FuzzyExtractor] Trying bit flip recovery...');
    const unstableIndices = this.getUnstableIndices(decoded.stabilityMask);
    console.log('[ZK FuzzyExtractor] Found', unstableIndices.length, 'unstable bits');

    const recoveredSeed = this.tryBitFlipCombinations(
      wPrimeBits,
      decoded.helper,
      decoded.checksum,
      unstableIndices.slice(0, MAX_BIT_FLIP_DEPTH)
    );

    if (recoveredSeed) {
      console.log('[ZK FuzzyExtractor] Recovered via bit flipping!');
      return this.deriveKey(recoveredSeed);
    }

    // 8. Last resort - if we have high-ish similarity, try encrypted seed with enrollment key
    if (decoded.enrollmentDescriptor && decoded.seed) {
      console.log('[ZK FuzzyExtractor] Trying enrollment-key decryption as last resort...');
      const enrollmentKey = deriveEncryptionKey(decoded.enrollmentDescriptor);
      const decryptedSeed = xorEncrypt(decoded.seed, enrollmentKey);
      const enrollmentChecksum = sha256(decryptedSeed).slice(0, 4);

      if (this.arraysEqual(enrollmentChecksum, decoded.checksum)) {
        console.log('[ZK FuzzyExtractor] Last resort decryption successful!');
        return this.deriveKey(decryptedSeed);
      }
    }

    console.log('[ZK FuzzyExtractor] All recovery methods failed');
    return null;
  }

  /**
   * Quantize descriptor to bits with stability tracking
   */
  private quantizeWithStability(descriptor: Float32Array): {
    bits: Uint8Array;
    stabilityMask: Uint8Array;
    uncertaintyScores: number[];
  } {
    const numBytes = Math.ceil(DESCRIPTOR_LENGTH / 8);
    const bits = new Uint8Array(numBytes);
    const stabilityMask = new Uint8Array(numBytes);
    const uncertaintyScores: number[] = [];

    for (let i = 0; i < DESCRIPTOR_LENGTH; i++) {
      const byteIndex = Math.floor(i / 8);
      const bitIndex = i % 8;

      // Normalize value to [0, 1] using sigmoid
      // face-api descriptors are roughly in [-0.5, 0.5] range
      const normalized = 1 / (1 + Math.exp(-descriptor[i] * 4)); // Scale factor 4 for better spread

      // Quantize to 0 or 1
      const bit = normalized >= 0.5 ? 1 : 0;
      bits[byteIndex] |= bit << bitIndex;

      // Calculate uncertainty (distance from 0.5)
      const uncertainty = 0.5 - Math.abs(normalized - 0.5);
      uncertaintyScores.push(uncertainty);

      // Mark as stable if far from threshold
      const isStable = Math.abs(normalized - 0.5) > 0.15 ? 1 : 0;
      stabilityMask[byteIndex] |= isStable << bitIndex;
    }

    return { bits, stabilityMask, uncertaintyScores };
  }

  /**
   * Get indices of unstable bits, sorted by uncertainty
   */
  private getUnstableIndices(stabilityMask: Uint8Array): number[] {
    const unstable: number[] = [];
    for (let i = 0; i < DESCRIPTOR_LENGTH; i++) {
      const byteIndex = Math.floor(i / 8);
      const bitIndex = i % 8;
      const isStable = (stabilityMask[byteIndex] >> bitIndex) & 1;
      if (!isStable) {
        unstable.push(i);
      }
    }
    return unstable;
  }

  /**
   * Try bit flip combinations to find matching checksum
   */
  private tryBitFlipCombinations(
    wPrimeBits: Uint8Array,
    helper: Uint8Array,
    targetChecksum: Uint8Array,
    unstableIndices: number[]
  ): Uint8Array | null {
    const numBits = Math.min(unstableIndices.length, MAX_BIT_FLIP_DEPTH);
    const maxCombos = Math.min(Math.pow(2, numBits), MAX_BIT_FLIP_ATTEMPTS);

    console.log('[ZK FuzzyExtractor] Trying', maxCombos, 'combinations on', numBits, 'bits');

    for (let combo = 0; combo < maxCombos; combo++) {
      const flippedBits = new Uint8Array(wPrimeBits);

      // Flip bits according to this combination
      for (let j = 0; j < numBits; j++) {
        if ((combo >> j) & 1) {
          const bitIndex = unstableIndices[j];
          const byteIndex = Math.floor(bitIndex / 8);
          const bitOffset = bitIndex % 8;
          flippedBits[byteIndex] ^= 1 << bitOffset;
        }
      }

      // Compute seed with flipped bits
      const noisySeed = this.xor(flippedBits, helper);
      const extractedSeed = noisySeed.slice(0, KEY_LENGTH);

      // Check checksum
      const computedChecksum = sha256(extractedSeed).slice(0, 4);
      if (this.arraysEqual(computedChecksum, targetChecksum)) {
        console.log('[ZK FuzzyExtractor] Found match at combination', combo);
        return extractedSeed;
      }
    }

    return null;
  }

  /**
   * Expand seed using HKDF
   */
  private expandSeed(seed: Uint8Array, targetLength: number): Uint8Array {
    const info = new TextEncoder().encode('zk-auth-expand-v3');
    return hkdf(sha256, seed, new Uint8Array(0), info, targetLength);
  }

  /**
   * XOR two byte arrays
   */
  private xor(a: Uint8Array, b: Uint8Array): Uint8Array {
    const result = new Uint8Array(Math.max(a.length, b.length));
    for (let i = 0; i < result.length; i++) {
      result[i] = (a[i] || 0) ^ (b[i] || 0);
    }
    return result;
  }

  /**
   * Derive key from seed
   */
  private deriveKey(seed: Uint8Array): Uint8Array {
    const info = new TextEncoder().encode('zk-auth-key-v3');
    return hkdf(sha256, seed, new Uint8Array(0), info, KEY_LENGTH);
  }

  /**
   * Check array equality
   */
  private arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  /**
   * Encode helper data for storage
   */
  private encodeHelper(data: EnhancedHelperData): string {
    // Convert descriptor to bytes
    const descriptorBytes = new Uint8Array(data.enrollmentDescriptor.buffer);

    // Format: version(1) + checksum(4) + stabilityMask(16) + seed(32) + descriptorLength(2) + descriptor + helper
    const descriptorLenBytes = new Uint8Array(2);
    descriptorLenBytes[0] = descriptorBytes.length & 0xFF;
    descriptorLenBytes[1] = (descriptorBytes.length >> 8) & 0xFF;

    const combined = concatBytes(
      new Uint8Array([data.version]),
      data.checksum,
      data.stabilityMask,
      data.seed,
      descriptorLenBytes,
      descriptorBytes,
      data.helper
    );

    return btoa(String.fromCharCode.apply(null, Array.from(combined)));
  }

  /**
   * Decode helper data
   */
  private decodeHelper(helperData: string): EnhancedHelperData {
    const combined = Uint8Array.from(atob(helperData), (c) => c.charCodeAt(0));
    const version = combined[0];

    if (version === 1) {
      // Legacy v1 format
      return {
        version: 1,
        checksum: combined.slice(1, 5),
        stabilityMask: new Uint8Array(16).fill(0xFF),
        enrollmentDescriptor: new Float32Array(0),
        seed: new Uint8Array(0),
        helper: combined.slice(5),
      };
    }

    if (version === 2) {
      // v2 format - try to parse
      const checksum = combined.slice(1, 5);
      const stabilityMask = combined.slice(5, 21);
      const helper = combined.slice(29);

      return {
        version: 2,
        checksum,
        stabilityMask,
        enrollmentDescriptor: new Float32Array(0),
        seed: new Uint8Array(0),
        helper,
      };
    }

    if (version === 3) {
      // v3 format with descriptor and encrypted seed
      const checksum = combined.slice(1, 5);
      const stabilityMask = combined.slice(5, 21);
      const seed = combined.slice(21, 53);
      const descriptorLen = combined[53] | (combined[54] << 8);
      const descriptorBytes = combined.slice(55, 55 + descriptorLen);
      const helper = combined.slice(55 + descriptorLen);

      // Convert descriptor bytes back to Float32Array
      const descriptorBuffer = new ArrayBuffer(descriptorLen);
      const descriptorView = new Uint8Array(descriptorBuffer);
      descriptorView.set(descriptorBytes);
      const enrollmentDescriptor = new Float32Array(descriptorBuffer);

      return {
        version: 3,
        checksum,
        stabilityMask,
        seed,
        enrollmentDescriptor,
        helper,
      };
    }

    throw new Error(`Unsupported helper data version: ${version}`);
  }
}

// ============================================================================
// Exports
// ============================================================================

export const fuzzyExtractor = new FuzzyExtractor();

export function averageDescriptors(descriptors: Float32Array[]): Float32Array {
  if (descriptors.length === 0) {
    throw new Error('No descriptors to average');
  }

  const length = descriptors[0].length;
  const result = new Float32Array(length);

  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (const descriptor of descriptors) {
      sum += descriptor[i];
    }
    result[i] = sum / descriptors.length;
  }

  console.log('[ZK FuzzyExtractor] Averaged', descriptors.length, 'descriptors');
  return result;
}

export function descriptorVariance(descriptors: Float32Array[]): number {
  if (descriptors.length < 2) return 0;

  const averaged = averageDescriptors(descriptors);
  let totalVariance = 0;

  for (const descriptor of descriptors) {
    for (let i = 0; i < descriptor.length; i++) {
      totalVariance += Math.pow(descriptor[i] - averaged[i], 2);
    }
  }

  return totalVariance / (descriptors.length * averaged.length);
}

export function generateFromFace(faceDescriptor: Float32Array): FuzzyExtractorResult {
  console.log('[ZK FuzzyExtractor] generateFromFace called');
  return fuzzyExtractor.gen(faceDescriptor);
}

export function generateFromMultipleFaces(descriptors: Float32Array[]): FuzzyExtractorResult {
  console.log('[ZK FuzzyExtractor] generateFromMultipleFaces with', descriptors.length, 'samples');
  const averaged = averageDescriptors(descriptors);
  return generateFromFace(averaged);
}

export function reproduceFromFace(
  faceDescriptor: Float32Array,
  helperData: string
): Uint8Array | null {
  console.log('[ZK FuzzyExtractor] reproduceFromFace called');
  return fuzzyExtractor.rep(faceDescriptor, helperData);
}

export function reproduceFromMultipleFaces(
  descriptors: Float32Array[],
  helperData: string
): Uint8Array | null {
  console.log('[ZK FuzzyExtractor] reproduceFromMultipleFaces with', descriptors.length, 'samples');
  const averaged = averageDescriptors(descriptors);
  return reproduceFromFace(averaged, helperData);
}

// ============================================================================
// Hybrid Fuzzy Extractor (v5) - Server Salt + Fuzzy Tolerance
// ============================================================================

/**
 * Hybrid approach combining server salt security with fuzzy tolerance.
 *
 * Enrollment:
 * - Generate random seed
 * - Quantize face to bits, XOR with expanded seed to create helper
 * - Key = HKDF(seed + serverSalt, "zk-auth-hybrid-v5")
 *
 * Authentication:
 * - Quantize face, XOR with helper to recover noisy seed
 * - Use bit-flip recovery to find exact seed (fuzzy tolerance)
 * - Key = HKDF(recoveredSeed + serverSalt)
 *
 * Benefits:
 * - Fuzzy tolerance for natural face variations
 * - Server salt prevents key derivation from face photo alone
 * - Need both face AND server access to derive key
 */

interface HybridHelperData {
  version: number;
  helper: Uint8Array; // XOR of quantized face and expanded seed
  checksum: Uint8Array; // sha256(seed).slice(0, 4) for verification
  stabilityMask: Uint8Array;
  enrollmentDescriptor: Float32Array; // For similarity check
  seed: Uint8Array; // Encrypted backup seed
  serverSaltHash: Uint8Array; // sha256(salt).slice(0, 4) to detect salt changes
}

/**
 * Generate key from face + server salt with fuzzy tolerance (Hybrid v5)
 */
export function generateDeterministicFromFace(
  faceDescriptor: Float32Array,
  serverSalt: string
): FuzzyExtractorResult {
  console.log('[ZK FuzzyExtractor v5] generateHybridFromFace called');
  console.log('[ZK FuzzyExtractor v5] Server salt:', serverSalt.substring(0, 16) + '...');

  if (faceDescriptor.length !== DESCRIPTOR_LENGTH) {
    throw new Error(`Invalid descriptor length: expected ${DESCRIPTOR_LENGTH}, got ${faceDescriptor.length}`);
  }

  // 1. Generate random seed (for fuzzy tolerance)
  const randomSeed = new Uint8Array(KEY_LENGTH);
  crypto.getRandomValues(randomSeed);
  console.log('[ZK FuzzyExtractor v5] Random seed generated');

  // 2. Quantize face to bits
  const { bits, stabilityMask } = quantizeWithStabilityV5(faceDescriptor);
  console.log('[ZK FuzzyExtractor v5] Quantized to', bits.length, 'bytes');

  // 3. Expand seed to match bits length and XOR to create helper
  const expandedSeed = expandSeedV5(randomSeed, bits.length);
  const helper = xorArrays(bits, expandedSeed);

  // 4. Convert server salt from hex to bytes
  const saltBytes = hexToBytes(serverSalt);

  // 5. Derive key from seed + salt (HYBRID: needs both!)
  const seedPlusSalt = concatBytes(randomSeed, saltBytes);
  const info = new TextEncoder().encode('zk-auth-hybrid-v5');
  const key = hkdf(sha256, seedPlusSalt, new Uint8Array(0), info, KEY_LENGTH);
  console.log('[ZK FuzzyExtractor v5] Key derived from seed + salt');

  // 6. Encrypt seed for backup recovery (using descriptor-derived key)
  const encryptionKey = deriveEncryptionKey(faceDescriptor);
  const encryptedSeed = xorEncrypt(randomSeed, encryptionKey);

  // 7. Create helper data
  const helperData = encodeHybridHelper({
    version: 5,
    helper,
    checksum: sha256(randomSeed).slice(0, 4), // Checksum of SEED, not key
    stabilityMask,
    enrollmentDescriptor: faceDescriptor,
    seed: encryptedSeed,
    serverSaltHash: sha256(saltBytes).slice(0, 4),
  });

  console.log('[ZK FuzzyExtractor v5] Helper data created, length:', helperData.length);

  return { key, helperData };
}

/**
 * Reproduce key from face + salt with fuzzy tolerance (Hybrid v5)
 */
export function reproduceDeterministicFromFace(
  faceDescriptor: Float32Array,
  helperData: string,
  serverSalt: string
): Uint8Array | null {
  console.log('[ZK FuzzyExtractor v5] reproduceHybridFromFace called');

  if (faceDescriptor.length !== DESCRIPTOR_LENGTH) {
    throw new Error('Invalid descriptor length');
  }

  // 1. Decode helper data
  const decoded = decodeHybridHelper(helperData);

  if (decoded.version !== 5) {
    console.log('[ZK FuzzyExtractor v5] Not a v5 helper data, version:', decoded.version);
    // Fall back to legacy reproduction for older versions
    return fuzzyExtractor.rep(faceDescriptor, helperData);
  }

  // 2. Convert server salt
  const saltBytes = hexToBytes(serverSalt);

  // 3. Check face similarity for early rejection
  if (decoded.enrollmentDescriptor && decoded.enrollmentDescriptor.length > 0) {
    const similarity = cosineSimilarity(faceDescriptor, decoded.enrollmentDescriptor);
    console.log('[ZK FuzzyExtractor v5] Face similarity:', similarity.toFixed(4));

    // Try direct seed decryption for high similarity
    if (similarity >= SIMILARITY_THRESHOLD_STRICT && decoded.seed) {
      console.log('[ZK FuzzyExtractor v5] High similarity! Trying direct seed decryption...');
      const encryptionKey = deriveEncryptionKey(faceDescriptor);
      const decryptedSeed = xorEncrypt(decoded.seed, encryptionKey);
      const computedChecksum = sha256(decryptedSeed).slice(0, 4);

      if (arraysEqualV5(computedChecksum, decoded.checksum)) {
        console.log('[ZK FuzzyExtractor v5] Direct decryption successful!');
        const seedPlusSalt = concatBytes(decryptedSeed, saltBytes);
        const info = new TextEncoder().encode('zk-auth-hybrid-v5');
        return hkdf(sha256, seedPlusSalt, new Uint8Array(0), info, KEY_LENGTH);
      }
      console.log('[ZK FuzzyExtractor v5] Direct decryption failed, trying fuzzy recovery...');
    }

    if (similarity < SIMILARITY_THRESHOLD_LOOSE) {
      console.log('[ZK FuzzyExtractor v5] Face too different, similarity below', SIMILARITY_THRESHOLD_LOOSE);
      return null;
    }
  }

  // 4. Quantize face and XOR with helper to get noisy seed
  const { bits } = quantizeWithStabilityV5(faceDescriptor);
  const noisySeed = xorArrays(bits, decoded.helper);
  const extractedSeed = noisySeed.slice(0, KEY_LENGTH);

  // 5. Check if exact match
  const computedChecksum = sha256(extractedSeed).slice(0, 4);
  if (arraysEqualV5(computedChecksum, decoded.checksum)) {
    console.log('[ZK FuzzyExtractor v5] Exact seed match!');
    const seedPlusSalt = concatBytes(extractedSeed, saltBytes);
    const info = new TextEncoder().encode('zk-auth-hybrid-v5');
    return hkdf(sha256, seedPlusSalt, new Uint8Array(0), info, KEY_LENGTH);
  }

  // 6. Try bit-flip recovery on unstable bits
  console.log('[ZK FuzzyExtractor v5] Trying bit-flip recovery...');
  const unstableIndices = getUnstableIndicesV5(decoded.stabilityMask);
  console.log('[ZK FuzzyExtractor v5] Found', unstableIndices.length, 'unstable bits');

  const recoveredSeed = tryBitFlipRecoveryV5(
    bits,
    decoded.helper,
    decoded.checksum,
    unstableIndices.slice(0, MAX_BIT_FLIP_DEPTH)
  );

  if (recoveredSeed) {
    console.log('[ZK FuzzyExtractor v5] Seed recovered via bit flipping!');
    const seedPlusSalt = concatBytes(recoveredSeed, saltBytes);
    const info = new TextEncoder().encode('zk-auth-hybrid-v5');
    return hkdf(sha256, seedPlusSalt, new Uint8Array(0), info, KEY_LENGTH);
  }

  // 7. Last resort - try enrollment key decryption
  if (decoded.enrollmentDescriptor && decoded.seed) {
    console.log('[ZK FuzzyExtractor v5] Trying enrollment-key decryption as last resort...');
    const enrollmentKey = deriveEncryptionKey(decoded.enrollmentDescriptor);
    const decryptedSeed = xorEncrypt(decoded.seed, enrollmentKey);
    const enrollmentChecksum = sha256(decryptedSeed).slice(0, 4);

    if (arraysEqualV5(enrollmentChecksum, decoded.checksum)) {
      console.log('[ZK FuzzyExtractor v5] Last resort decryption successful!');
      const seedPlusSalt = concatBytes(decryptedSeed, saltBytes);
      const info = new TextEncoder().encode('zk-auth-hybrid-v5');
      return hkdf(sha256, seedPlusSalt, new Uint8Array(0), info, KEY_LENGTH);
    }
  }

  console.log('[ZK FuzzyExtractor v5] All recovery methods failed');
  return null;
}

/**
 * Quantize descriptor to bits with stability tracking (v5 version)
 */
function quantizeWithStabilityV5(descriptor: Float32Array): {
  bits: Uint8Array;
  stabilityMask: Uint8Array;
} {
  const numBytes = Math.ceil(DESCRIPTOR_LENGTH / 8);
  const bits = new Uint8Array(numBytes);
  const stabilityMask = new Uint8Array(numBytes);

  for (let i = 0; i < DESCRIPTOR_LENGTH; i++) {
    const byteIndex = Math.floor(i / 8);
    const bitIndex = i % 8;

    // Normalize to [0, 1] using sigmoid
    const normalized = 1 / (1 + Math.exp(-descriptor[i] * 4));

    // Quantize
    const bit = normalized >= 0.5 ? 1 : 0;
    bits[byteIndex] |= bit << bitIndex;

    // Mark stability (more lenient threshold for better tolerance)
    const isStable = Math.abs(normalized - 0.5) > 0.12 ? 1 : 0;
    stabilityMask[byteIndex] |= isStable << bitIndex;
  }

  return { bits, stabilityMask };
}

/**
 * Expand seed using HKDF (v5)
 */
function expandSeedV5(seed: Uint8Array, targetLength: number): Uint8Array {
  const info = new TextEncoder().encode('zk-auth-expand-v5');
  return hkdf(sha256, seed, new Uint8Array(0), info, targetLength);
}

/**
 * XOR two byte arrays
 */
function xorArrays(a: Uint8Array, b: Uint8Array): Uint8Array {
  const result = new Uint8Array(Math.max(a.length, b.length));
  for (let i = 0; i < result.length; i++) {
    result[i] = (a[i] || 0) ^ (b[i] || 0);
  }
  return result;
}

/**
 * Get indices of unstable bits (v5)
 */
function getUnstableIndicesV5(stabilityMask: Uint8Array): number[] {
  const unstable: number[] = [];
  for (let i = 0; i < DESCRIPTOR_LENGTH; i++) {
    const byteIndex = Math.floor(i / 8);
    const bitIndex = i % 8;
    const isStable = (stabilityMask[byteIndex] >> bitIndex) & 1;
    if (!isStable) {
      unstable.push(i);
    }
  }
  return unstable;
}

/**
 * Try bit flip combinations to recover seed (v5 - fuzzy recovery)
 */
function tryBitFlipRecoveryV5(
  bits: Uint8Array,
  helper: Uint8Array,
  targetChecksum: Uint8Array,
  unstableIndices: number[]
): Uint8Array | null {
  const numBits = Math.min(unstableIndices.length, MAX_BIT_FLIP_DEPTH);
  const maxCombos = Math.min(Math.pow(2, numBits), MAX_BIT_FLIP_ATTEMPTS);

  console.log('[ZK FuzzyExtractor v5] Trying', maxCombos, 'combinations on', numBits, 'bits');

  for (let combo = 0; combo < maxCombos; combo++) {
    const flippedBits = new Uint8Array(bits);

    for (let j = 0; j < numBits; j++) {
      if ((combo >> j) & 1) {
        const bitIndex = unstableIndices[j];
        const byteIndex = Math.floor(bitIndex / 8);
        const bitOffset = bitIndex % 8;
        flippedBits[byteIndex] ^= 1 << bitOffset;
      }
    }

    // XOR with helper to recover seed
    const noisySeed = xorArrays(flippedBits, helper);
    const extractedSeed = noisySeed.slice(0, KEY_LENGTH);

    // Check checksum (of SEED, not key)
    const computedChecksum = sha256(extractedSeed).slice(0, 4);
    if (arraysEqualV5(computedChecksum, targetChecksum)) {
      console.log('[ZK FuzzyExtractor v5] Found match at combination', combo);
      return extractedSeed;
    }
  }

  return null;
}

/**
 * Arrays equal helper (v5)
 */
function arraysEqualV5(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Convert hex string to bytes
 */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

/**
 * Encode hybrid helper data (v5)
 */
function encodeHybridHelper(data: HybridHelperData): string {
  const descriptorBytes = new Uint8Array(data.enrollmentDescriptor.buffer);

  const descriptorLenBytes = new Uint8Array(2);
  descriptorLenBytes[0] = descriptorBytes.length & 0xFF;
  descriptorLenBytes[1] = (descriptorBytes.length >> 8) & 0xFF;

  const helperLenBytes = new Uint8Array(2);
  helperLenBytes[0] = data.helper.length & 0xFF;
  helperLenBytes[1] = (data.helper.length >> 8) & 0xFF;

  // Format: version(1) + checksum(4) + stabilityMask(16) + seed(32) + serverSaltHash(4) +
  //         helperLen(2) + helper + descriptorLen(2) + descriptor
  const combined = concatBytes(
    new Uint8Array([data.version]),
    data.checksum,
    data.stabilityMask,
    data.seed,
    data.serverSaltHash,
    helperLenBytes,
    data.helper,
    descriptorLenBytes,
    descriptorBytes
  );

  return btoa(String.fromCharCode.apply(null, Array.from(combined)));
}

/**
 * Decode hybrid helper data (v5)
 */
function decodeHybridHelper(helperData: string): HybridHelperData {
  const combined = Uint8Array.from(atob(helperData), (c) => c.charCodeAt(0));
  const version = combined[0];

  if (version !== 5) {
    // Return minimal data for non-v5 formats
    return {
      version,
      helper: new Uint8Array(0),
      checksum: new Uint8Array(4),
      stabilityMask: new Uint8Array(16),
      enrollmentDescriptor: new Float32Array(0),
      seed: new Uint8Array(0),
      serverSaltHash: new Uint8Array(4),
    };
  }

  // Parse v5 format
  const checksum = combined.slice(1, 5);
  const stabilityMask = combined.slice(5, 21);
  const seed = combined.slice(21, 53);
  const serverSaltHash = combined.slice(53, 57);
  const helperLen = combined[57] | (combined[58] << 8);
  const helper = combined.slice(59, 59 + helperLen);
  const descriptorLenOffset = 59 + helperLen;
  const descriptorLen = combined[descriptorLenOffset] | (combined[descriptorLenOffset + 1] << 8);
  const descriptorBytes = combined.slice(descriptorLenOffset + 2, descriptorLenOffset + 2 + descriptorLen);

  // Convert to Float32Array
  const descriptorBuffer = new ArrayBuffer(descriptorLen);
  const descriptorView = new Uint8Array(descriptorBuffer);
  descriptorView.set(descriptorBytes);
  const enrollmentDescriptor = new Float32Array(descriptorBuffer);

  return {
    version,
    helper,
    checksum,
    stabilityMask,
    enrollmentDescriptor,
    seed,
    serverSaltHash,
  };
}
